import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Input, InputNumber, Select, Button, Space } from 'antd';
import { useConfigQuery } from '../../../queries/useConfig';

const { Option } = Select;

// ====== Helpers para documento CPF/CNPJ ======
const onlyDigits = (val = '') => {
    return val.replace(/\D/g, '').slice(0, 14); // máximo 14 dígitos
};

const formatDocumento = (digits = '') => {
    if (!digits) return '';

    // CPF até 11 dígitos -> 000.000.000-00
    if (digits.length <= 11) {
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return digits.replace(/(\d{3})(\d{1,3})/, '$1.$2');
        if (digits.length <= 9)
            return digits.replace(/(\d{3})(\d{3})(\d{1,3})/, '$1.$2.$3');
        return digits.replace(
            /(\d{3})(\d{3})(\d{3})(\d{1,2})/,
            '$1.$2.$3-$4'
        );
    }

    // CNPJ 12–14 dígitos -> 00.000.000/0000-00
    if (digits.length <= 2) return digits;
    if (digits.length <= 5)
        return digits.replace(/(\d{2})(\d{1,3})/, '$1.$2');
    if (digits.length <= 8)
        return digits.replace(/(\d{2})(\d{3})(\d{1,3})/, '$1.$2.$3');
    if (digits.length <= 12)
        return digits.replace(
            /(\d{2})(\d{3})(\d{3})(\d{1,4})/,
            '$1.$2.$3/$4'
        );
    return digits.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{1,2})/,
        '$1.$2.$3/$4-$5'
    );
};

// ====== Helpers para pesos (BR visual, 4 casas com ponto no objeto) ======
const formatPesoBRFromStorage = (stored) => {
    if (stored === undefined || stored === null || stored === '') return '';

    const num = Number(stored);
    if (Number.isNaN(num)) return '';

    const fixed = num.toFixed(4); // "1234.5678"
    const [intPart, decPart] = fixed.split('.');

    const intWithSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.'); // milhar

    return `${intWithSep},${decPart}`;
};

// converte texto digitado ("1.234,5678" ou "1234,5") -> "1234.5678"
const parsePesoBRToStorage = (raw) => {
    if (!raw) return '';

    // mantém apenas dígitos, vírgula e ponto
    let cleaned = raw.replace(/[^\d,\.]/g, '');

    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    const sepIndex = Math.max(lastComma, lastDot);

    let intPart;
    let decPart;

    if (sepIndex === -1) {
        intPart = cleaned;
        decPart = '';
    } else {
        intPart = cleaned.slice(0, sepIndex);
        decPart = cleaned.slice(sepIndex + 1);
    }

    intPart = intPart.replace(/\D/g, '');
    decPart = decPart.replace(/\D/g, '');

    if (!intPart && !decPart) return '';

    const joined = decPart ? `${intPart}.${decPart}` : intPart;
    const num = Number(joined);
    if (Number.isNaN(num)) return '';

    return num.toFixed(4); // "1234.5678"
};

// ====== Defaults ======
const getDefaultVolume = () => ({
    qVol: '',
    esp: '',
    marca: '',
    nVol: '',
    pesoL_transp: '',
    pesoB_transp: '',
});

export const getDefaultTransp = () => ({
    modFrete: '9'
});

const TranspForm = ({ value, onChange }) => {
    const { data: config, error } = useConfigQuery();
    const [transp, setTransp] = useState(() => value || getDefaultTransp());
    const [pesoInputs, setPesoInputs] = useState({}); // controle de texto dos pesos

    useEffect(() => {
        if (value) {
            setTransp(value);
        }
    }, [value]);

    const updateTransp = (patch) => {
        console.log(patch);
        if (patch.modFrete == '3') {
            const transpAux = {
                ...transp,
                transporta: {
                    ...(transp?.transporta || {}),
                    CNPJ_transp: config?.restaurante?.cnpj || '',
                    xNome_transp: config?.restaurante?.nome || '',
                    IE_transp: config?.restaurante?.ie || '',
                    UF_transp: config?.restaurante?.endereco?.cidade?.estado || '',
                    xMun_transp: config?.restaurante?.endereco?.cidade?.nome || '',
                    xEnder: config?.restaurante?.endereco?.logradouro || '',
                },
            };
            const newTransp = { ...transpAux, ...patch };
            setTransp(newTransp);
            if (onChange) onChange(newTransp);

        } else {
            const newTransp = { ...transp, ...patch }; 
            setTransp(newTransp);
            if (onChange) onChange(newTransp);
        }

    };

    const handleBasicChange = (field, val) => {

        updateTransp({ [field]: val });
    };

    const handleTransportaChange = (field, val) => {
        updateTransp({
            transporta: {
                ...(transp.transporta || {}),
                [field]: val,
            },
        });
    };

    const handleVeicTranspChange = (field, val) => {
        updateTransp({
            veicTransp: {
                ...(transp.veicTransp || {}),
                [field]: val,
            },
        });
    };

    // Volumes
    const handleVolChange = (index, field, val) => {
        const list = transp.vol ? [...transp.vol] : [];
        list[index] = { ...(list[index] || getDefaultVolume()), [field]: val };
        updateTransp({ vol: list });
    };

    const handleAddVol = () => {
        const list = transp.vol ? [...transp.vol] : [];
        let novoVolume = getDefaultVolume();
        novoVolume.nVol = list.length + 1;
        console.log(novoVolume);
        console.log(list);
        list.push(novoVolume);
        console.log(list);
        updateTransp({ vol: list });

    };

    const handleRemoveVol = (index) => {
        const list = transp.vol ? [...transp.vol] : [];
        list.splice(index, 1);
        updateTransp({ vol: list });
    };

    // Documento CPF/CNPJ
    const handleTransportaDocumentoChange = (rawVal) => {
        const digits = onlyDigits(rawVal);

        const patchBase = {
            CNPJ_transp: '',
            CPF_transp: '',
        };

        let patch = { ...patchBase };

        if (digits.length <= 11) {
            patch.CPF_transp = digits;
        } else {
            patch.CNPJ_transp = digits;
        }

        updateTransp({
            transporta: {
                ...(transp.transporta || {}),
                ...patch,
            },
        });
    };

    const getTransportaDocumento = () => {
        const digits =
            transp.transporta?.CNPJ_transp ||
            transp.transporta?.CPF_transp ||
            '';
        return formatDocumento(digits);
    };

    // Pesos (controle de input em BR)
    const handlePesoInputChange = (vIndex, field, rawVal) => {
        const key = `${vIndex}-${field}`;
        setPesoInputs((prev) => ({
            ...prev,
            [key]: rawVal,
        }));
    };

    const handlePesoInputBlur = (vIndex, field) => {
        const key = `${vIndex}-${field}`;
        const rawVal = pesoInputs[key] ?? '';

        const stored = parsePesoBRToStorage(rawVal); // "1234.5678" ou ""

        // grava no objeto transp
        handleVolChange(vIndex, field, stored);

        // atualiza o que aparece no input (formatado BR)
        setPesoInputs((prev) => ({
            ...prev,
            [key]: formatPesoBRFromStorage(stored),
        }));
    };

    return (
        <Card title="Transporte (transp)">
            {/* Linha básica */}
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <label>Modalidade do frete (modFrete)</label>
                    <Select
                        style={{ width: '100%' }}
                        value={transp.modFrete}
                        onChange={(val) => handleBasicChange('modFrete', val)}
                    >
                        <Option value="0">0 - Contratação do Frete por conta do Remetente (CIF)</Option>
                        <Option value="1">1 - Contratação do Frete por conta do Destinatário (FOB)</Option>
                        <Option value="2">2 - Contratação do Frete por conta de Terceiros</Option>
                        <Option value="3">3 - Transporte Próprio por conta do Remetente</Option>
                        <Option value="4">4 - Transporte Próprio por conta do Destinatário</Option>
                        <Option value="9">9 - Sem Ocorrência de Transporte</Option>
                    </Select>
                </Col>
            </Row>
            {transp.modFrete != 9 ? <>
                {/* Transportador */}
                <Card size="small" style={{ marginTop: 16 }} title="Transportador (transporta)">
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <label>Documento (CPF/CNPJ)</label>
                            <Input
                                value={getTransportaDocumento()}
                                onChange={(e) => handleTransportaDocumentoChange(e.target.value)}
                            />
                        </Col>
                        <Col xs={24} md={16}>
                            <label>Nome / Razão Social</label>
                            <Input
                                value={transp.transporta?.xNome_transp}
                                onChange={(e) => handleTransportaChange('xNome_transp', e.target.value)}
                            />
                        </Col>
                    </Row>

                    <Row gutter={16} style={{ marginTop: 12 }}>
                        <Col xs={24} md={6}>
                            <label>IE</label>
                            <Input
                                value={transp.transporta?.IE_transp}
                                onChange={(e) => handleTransportaChange('IE_transp', e.target.value)}
                            />
                        </Col>
                        <Col xs={24} md={10}>
                            <label>Endereço</label>
                            <Input
                                value={transp.transporta?.xEnder}
                                onChange={(e) => handleTransportaChange('xEnder', e.target.value)}
                            />
                        </Col>
                        <Col xs={24} md={4}>
                            <label>Município</label>
                            <Input
                                value={transp.transporta?.xMun_transp}
                                onChange={(e) => handleTransportaChange('xMun_transp', e.target.value)}
                            />
                        </Col>
                        <Col xs={24} md={4}>
                            <label>UF</label>
                            <Input
                                maxLength={2}
                                value={transp.transporta?.UF_transp}
                                onChange={(e) =>
                                    handleTransportaChange('UF_transp', e.target.value.toUpperCase())
                                }
                            />
                        </Col>
                    </Row>
                </Card>

                {/* Veículo */}
                <Card size="small" style={{ marginTop: 16 }} title="Veículo (veicTransp)">
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <label>Placa</label>
                            <Input
                                value={transp.veicTransp?.placa}
                                onChange={(e) => handleVeicTranspChange('placa', e.target.value)}
                            />
                        </Col>
                        <Col xs={24} md={4}>
                            <label>UF</label>
                            <Input
                                maxLength={2}
                                value={transp.veicTransp?.UF_veictransp}
                                onChange={(e) =>
                                    handleVeicTranspChange('UF_veictransp', e.target.value.toUpperCase())
                                }
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <label>RNTC</label>
                            <Input
                                value={transp.veicTransp?.RNTC}
                                onChange={(e) => handleVeicTranspChange('RNTC', e.target.value)}
                            />
                        </Col>
                    </Row>
                </Card>

                {/* Volumes */}
                <Card
                    size="small"
                    style={{ marginTop: 16 }}
                    title={
                        <Space>
                            Volumes (vol)
                            <Button size="small" onClick={handleAddVol}>
                                Adicionar volume
                            </Button>
                        </Space>
                    }
                >
                    {transp.vol?.map((vol, vIndex) => (
                        <Card
                            key={vIndex}
                            size="small"
                            type="inner"
                            title={`Volume #${vIndex + 1}`}
                            extra={
                                <Button size="small" danger onClick={() => handleRemoveVol(vIndex)}>
                                    Remover
                                </Button>
                            }
                            style={{ marginBottom: 12 }}
                        >
                            <Row gutter={16}>
                                <Col xs={24} md={4}>
                                    <label>qVol</label>
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        value={vol.qVol}
                                        onChange={(v) => handleVolChange(vIndex, 'qVol', v)}
                                    />
                                </Col>
                                <Col xs={24} md={4}>
                                    <label>Espécie</label>
                                    <Input
                                        value={vol.esp}
                                        onChange={(e) => handleVolChange(vIndex, 'esp', e.target.value)}
                                    />
                                </Col>
                                <Col xs={24} md={4}>
                                    <label>Marca</label>
                                    <Input
                                        value={vol.marca}
                                        onChange={(e) => handleVolChange(vIndex, 'marca', e.target.value)}
                                    />
                                </Col>
                                <Col xs={24} md={4}>
                                    <label>Nº</label>
                                    <Input
                                        value={vol.nVol}
                                        onChange={(e) => handleVolChange(vIndex, 'nVol', e.target.value)}
                                    />
                                </Col>

                                <Col xs={24} md={4}>
                                    <label>Peso Líq. (kg)</label>
                                    <Input
                                        style={{ width: '100%' }}
                                        value={
                                            pesoInputs[`${vIndex}-pesoL_transp`] ??
                                            formatPesoBRFromStorage(vol.pesoL_transp)
                                        }
                                        onChange={(e) =>
                                            handlePesoInputChange(vIndex, 'pesoL_transp', e.target.value)
                                        }
                                        onBlur={() => handlePesoInputBlur(vIndex, 'pesoL_transp')}
                                    />
                                </Col>

                                <Col xs={24} md={4}>
                                    <label>Peso Bruto (kg)</label>
                                    <Input
                                        style={{ width: '100%' }}
                                        value={
                                            pesoInputs[`${vIndex}-pesoB_transp`] ??
                                            formatPesoBRFromStorage(vol.pesoB_transp)
                                        }
                                        onChange={(e) =>
                                            handlePesoInputChange(vIndex, 'pesoB_transp', e.target.value)
                                        }
                                        onBlur={() => handlePesoInputBlur(vIndex, 'pesoB_transp')}
                                    />
                                </Col>
                            </Row>
                        </Card>
                    ))}
                </Card>
            </> : null}









        </Card>
    );
};

export default TranspForm;
